import { useQuery, useMutation, UseQueryOptions, UseQueryResult, UseMutationOptions, UseMutationResult, QueryFunctionContext } from '@tanstack/react-query';
import { useMode } from '@/providers/ModeProvider';
import { useCompany } from '@/contexts/CompanyContext';

/**
 * Custom hook that wraps useQuery and respects the Demo/Live mode
 * 
 * @param queryKey - The query key for caching
 * @param liveFn - Function to fetch live data from backend (receives QueryFunctionContext with signal for cancellation)
 * @param demoData - Mock data to use in demo mode
 * @param options - Additional query options
 * @returns UseQueryResult with data based on current mode
 * 
 * @example
 * const { apiGet } = useApi();
 * const { data, isLoading } = useModeAwareQuery(
 *   ['customers'],
 *   async ({ signal }) => {
 *     return apiGet<CustomersApiResponse>('/api/v1/customers', undefined, signal);
 *   },
 *   mockCustomers,
 *   { staleTime: 5 * 60 * 1000 }
 * );
 */
export function useModeAwareQuery<TData = unknown, TError = Error>(
  queryKey: unknown[],
  liveFn: (ctx: QueryFunctionContext) => Promise<TData>,
  demoData: TData,
  options?: Omit<UseQueryOptions<TData, TError>, 'queryKey' | 'queryFn'>
): UseQueryResult<TData, TError> {
  const { isDemo } = useMode();
  const { selectedCompany } = useCompany();

  return useQuery<TData, TError>({
    queryKey: [...queryKey, isDemo ? 'demo' : 'live', selectedCompany?.id ?? 'no-company'],
    queryFn: async (ctx) => {
      if (isDemo) {
        // Simulate network delay in demo mode for realistic UX
        // Make the delay cancellable as well
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(resolve, 300);
          if (ctx.signal) {
            ctx.signal.addEventListener('abort', () => {
              clearTimeout(timeout);
              // Match fetch's behaviour: reject with an AbortError-like object
              reject(new DOMException('Aborted', 'AbortError'));
            });
          }
        });
        return demoData;
      }
      return liveFn(ctx);
    },
    ...options,
  });
}

/**
 * Hook to get data synchronously based on mode
 * Useful for components that don't need async loading states
 * 
 * @param liveData - Data from live backend
 * @param demoData - Mock data for demo mode
 * @returns Data based on current mode
 */
export function useModeAwareData<TData>(liveData: TData, demoData: TData): TData {
  const { isDemo } = useMode();
  return isDemo ? demoData : liveData;
}

/**
 * Custom hook that wraps useMutation and respects the Demo/Live mode
 * 
 * @param liveFn - Function to call in live mode (actual API call)
 * @param demoFn - Function to call in demo mode (mock response)
 * @param options - Additional mutation options
 * @returns UseMutationResult based on current mode
 * 
 * @example
 * const { apiPost } = useApi();
 * const createUser = useModeAwareMutation(
 *   async (data) => apiPost('/api/v1/users', data),
 *   async (data) => ({ id: Date.now(), ...data }), // mock response
 *   { onSuccess: () => queryClient.invalidateQueries(['users']) }
 * );
 */
export function useModeAwareMutation<TData = unknown, TError = Error, TVariables = void>(
  liveFn: (variables: TVariables) => Promise<TData>,
  demoFn: (variables: TVariables) => TData | Promise<TData>,
  options?: Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>
): UseMutationResult<TData, TError, TVariables> {
  const { isDemo } = useMode();

  return useMutation<TData, TError, TVariables>({
    mutationFn: async (variables) => {
      if (isDemo) {
        // Simulate network delay in demo mode
        await new Promise(resolve => setTimeout(resolve, 300));
        return demoFn(variables);
      }
      return liveFn(variables);
    },
    ...options,
  });
}
